import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import useLaborMasterDataInit, { LaborSelection, OPTIONAL_LABOR_ITEMS, LABOR_ORDER } from "./useLaborMasterDataInit";
import { useLaborUpdate } from "./useLaborUpdate";
import { useLaborDatabaseSync } from "./useLaborDatabaseSync";
import { useInspectionAttributes } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { useLaborFormulaEvaluator } from "../../hooks";

/**
 * useLabor (Orchestrator)
 * -----------------------
 *   1. useLaborMasterDataInit → loads data from server
 *   2. (useLaborUpdate)       → pure state updates on user actions (next step)
 *   3. (useLaborDatabaseSync) → syncs selectedValues to DB after every real change (next step)
 */
export function useLabor({ measurements }: { measurements: any[] }) {
    // ── Inspection Attributes ────────────────────────────────────────────────
    const { project } = useProject();
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const listAttributesSvc = useListAttributes();
    const [attributes, setAttributes] = useState<any[]>([]);

    useEffect(() => {
        if (!project?.id) return;
        listAttributesSvc.mutateAsync({ ProjectId: project.id }).then(setAttributes).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    // ── State ────────────────────────────────────────────────────────────────
    const [laborSchema, setLaborSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [selectedValues, setSelectedValues] = useState<Record<string, LaborSelection>>({});

    const syncReadyRef = useRef(false);

    // ── Sync ─────────────────────────────────────────────────────────────────
    const { initializeDbState, syncToDatabase } = useLaborDatabaseSync();

    useEffect(() => {
        if (!syncReadyRef.current) return;

        // Debouncing happens implicitly by how fast the user types but we could easily wrap this.
        // It uses the same pattern as materials.
        const timer = setTimeout(() => {
            void syncToDatabase(selectedValues);
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [selectedValues, syncToDatabase]);

    // ── Hook: Update ─────────────────────────────────────────────────────────
    const { handleQuantityUpdate, handleClearLabor } = useLaborUpdate({ setSelectedValues });

    const handleClearLaborEx = useCallback(
        (laborServiceId: string) => {
            handleClearLabor(laborServiceId);

            // Also remove from laborSchema if it's in OPTIONAL_LABOR_ITEMS
            setLaborSchema((prevSchema) => {
                const itemToRemove = prevSchema.find((item) => item.Id === laborServiceId);
                if (itemToRemove && OPTIONAL_LABOR_ITEMS.includes(itemToRemove.Name)) {
                    return prevSchema.filter((item) => item.Id !== laborServiceId);
                }
                return prevSchema;
            });
        },
        [handleClearLabor, setLaborSchema],
    );

    // ── Hook: Init ───────────────────────────────────────────────────────────
    const { loadLaborData, fetchCartItems, allLaborServices } = useLaborMasterDataInit({
        measurements,
        attributes,
        setLaborSchema,
        setCart,
        setPriceRanges,
        setIsLoading,
        initializeDbState,
        setSelectedValues: (values) => {
            setSelectedValues(values);
            syncReadyRef.current = true; // From now on, all changes will sync
        },
    });

    const handleAddLaborItem = useCallback((serviceId: string) => {
        const service = allLaborServices.find((item: any) => item.Id === serviceId);
        if (!service) return;

        // Add to laborSchema state if not already there
        setLaborSchema((prevSchema) => {
            if (prevSchema.some((item) => item.Id === serviceId)) return prevSchema;
            const updated = [...prevSchema, service];
            
            // Sort it to maintain the correct sorted order
            updated.sort((a, b) => {
                const indexA = LABOR_ORDER.indexOf(a.Name);
                const indexB = LABOR_ORDER.indexOf(b.Name);
                if (indexA === -1 && indexB === -1) return a.Name.localeCompare(b.Name);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
            return updated;
        });

        // Set quantity to 1 in selectedValues
        setSelectedValues((prev) => {
            return {
                ...prev,
                [serviceId]: {
                    ...prev[serviceId],
                    quantity: 1,
                    unitPrice: 0,
                },
            };
        });
    }, [allLaborServices, setSelectedValues, setLaborSchema]);

    // ── Adapter for Supplier Comparison ──────────────────────────────────────
    const adaptedSelectedValues = useMemo(() => {
        const adapted: Record<string, any> = {};
        laborSchema.forEach((schemaItem) => {
            const selection = selectedValues[schemaItem.Id];
            if (selection && selection.quantity > 0) {
                adapted[schemaItem.Id] = {
                    id: selection.id || selection.CartItemId,
                    value: schemaItem.Name, // backend 'ProductName' expects this value
                    supplierName: selection.supplierName,
                    supplierId: selection.supplierId,
                    quantity: selection.quantity,
                    unitPrice: selection.unitPrice,
                };
            }
        });
        return adapted;
    }, [laborSchema, selectedValues]);

    const { evaluateFormula } = useLaborFormulaEvaluator();

    const handleReevaluateFormula = useCallback((serviceId: string) => {
        setSelectedValues((prev) => {
            const formulaResults = evaluateFormula(measurements, laborSchema, {}, attributes, cart);
            const calculatedQty = formulaResults[serviceId] || 0;
            return {
                ...prev,
                [serviceId]: {
                    ...prev[serviceId],
                    quantity: calculatedQty,
                },
            };
        });
    }, [measurements, laborSchema, attributes, evaluateFormula, setSelectedValues, cart]);

    // ── Expose ───────────────────────────────────────────────────────────────
    return {
        laborSchema,
        cart,
        priceRanges,
        isLoading,
        selectedValues,
        adaptedSelectedValues,
        loadLaborData,
        fetchCartItems,
        allLaborServices,
        handleQuantityUpdate,
        handleClearLabor: handleClearLaborEx,
        handleReevaluateFormula,
        handleAddLaborItem,
    };
}
